import { useState, useEffect, useCallback, useRef } from 'react';

import { IconButton, Table, TableContainer, TableHead, TableBody, Paper } from '@mui/material';
import { StopCircle, PlayCircle } from '@mui/icons-material';

import { D, Loading, TRow, TCell } from '../include';

// ==========================================
// COMPONENTE INTERNO: DaemonRow
// ==========================================
const DaemonRow = ({ row, api }) => {
  const [status, setStatus] = useState(null); // null = carregando
  
  // Ref para proteger a montagem da linha (evita duplo fetchStatus no Strict Mode)
  const isFirstRender = useRef(true);

  // useCallback evita recriação desnecessária da função
  const fetchStatus = useCallback(() => {
    api.post('Daemon/status', { name: row.alias })
       .then(r => setStatus(r.status))
       .catch(err => {
         console.error("Erro ao buscar status do daemon:", err);
         setStatus('error');
       });
  }, [api, row.alias]);

  // Busca o status de forma protegida
  useEffect(() => {
    if (isFirstRender.current) {
      fetchStatus();
      isFirstRender.current = false;
    }
  }, [fetchStatus]);

  // Função para ligar/desligar
  const toggleStatus = () => {
    const action = status === 'off' ? 'start' : 'stop';
    setStatus(null); // Volta pro Loading visual enquanto a API processa

    api.post(`/Daemon/${action}`, { name: row.alias })
       .then(() => fetchStatus()) // Rebusca o status real pós-ação
       .catch(err => {
         console.error("Erro ao alterar daemon:", err);
         fetchStatus(); // Em caso de erro, tenta recuperar o status real
       });
  };

  return (
    <TRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TCell component="th" align="center">
        <IconButton 
          style={{ color: '#aaa' }} 
          onClick={toggleStatus} 
          disabled={!status || status === 'error'} // Proteção contra multi-clicks
        >
          {status === 'off' ? <PlayCircle /> : <StopCircle />}
        </IconButton>
      </TCell>

      {/* Renderização condicional do status */}
      <TCell component="th" align="center">
        {!status ? (
          <Loading />
        ) : status === 'off' ? (
          <span style={{ color: 'red', fontWeight: 'bold' }}>Inativo</span>
        ) : status === 'error' ? (
          <span style={{ color: 'orange', fontWeight: 'bold' }}>Erro</span>
        ) : (
          <span style={{ color: 'green', fontWeight: 'bold' }}>Ativo</span>
        )}
      </TCell>

      <TCell component="th">{window.location.hostname}</TCell>
      <TCell component="th">{row.alias}</TCell>
      <TCell component="th">{row.name}</TCell>
    </TRow>
  );
};


// ==========================================
// COMPONENTE PRINCIPAL: Daemons
// ==========================================
export const Daemons = ({ api }) => {
  const [daemons, setDaemons] = useState(null); // null para saber que está na carga inicial
  
  // Ref para proteger a montagem da tabela principal
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false; // Tranca a porta para o Strict Mode
      
      api.get('/Daemons')
         .then(data => {
           setDaemons(data || []); // Sem checagem de isMounted, apenas atualiza
         })
         .catch(err => {
           console.error("Erro ao buscar lista de daemons:", err);
           setDaemons([]);
         });
    }
  }, [api]);

  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TRow>
                <TCell align="center">Controle</TCell>
                <TCell align="center">Status</TCell>
                <TCell>Endereço</TCell>
                <TCell>Alias</TCell>
                <TCell width="100%">Nome</TCell>
              </TRow>
            </TableHead>
            
            <TableBody>
              {daemons === null ? (
                // Carga inicial
                <TRow>
                  <TCell component="th" align="center" colSpan={5}>
                    <Loading />
                  </TCell>
                </TRow>
              ) : daemons.length > 0 ? (
                // Lista de serviços
                daemons.map((row, index) => (
                  <DaemonRow 
                    key={row.alias || index} 
                    row={row} 
                    api={api} 
                  />
                ))
              ) : (
                // Lista vazia
                <TRow>
                  <TCell component="th" align="center" colSpan={5}>
                    Nenhum daemon encontrado
                  </TCell>
                </TRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </D>
  );
};